import React from 'react'
import PropTypes from 'prop-types'
import {
  Typography,
  Box,
  Paper,
  Grid,
  List,
  ListItem,
  Chip
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Assessment as AssessmentIcon
} from '@material-ui/icons'
import Chart from 'react-apexcharts'
import strings from 'strings'

const useStyles = makeStyles((theme) => ({
  container: {
    backgroundColor: 'white',
    minHeight: '100vh'
  },
  section: {
    marginBottom: theme.spacing(3)
  },
  summaryCard: {
    padding: theme.spacing(3),
    borderRadius: '12px',
    border: '1px solid #E0E0E0',
    boxShadow: 'none',
    height: '100%'
  },
  summaryCardPositive: {
    borderColor: '#4CAF50',
    backgroundColor: '#F3F8F3',
  },
  summaryCardNegative: {
    borderColor: '#F44336',
    backgroundColor: '#FFF3F3',
  },
  summaryCardNeutral: {
    borderColor: '#FF9800',
    backgroundColor: '#FFF8F3',
  },
  metricValue: {
    fontWeight: 700,
    fontSize: '2rem',
    marginBottom: theme.spacing(1)
  },
  metricValuePositive: {
    color: '#4CAF50'
  },
  metricValueNegative: {
    color: '#F44336'
  },
  metricValueNeutral: {
    color: '#FF9800'
  },
  metricLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    fontWeight: 500
  },
  metricSubtext: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    marginTop: theme.spacing(0.5)
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    marginBottom: theme.spacing(1)
  },
  iconContainerPositive: {
    backgroundColor: '#E8F5E9',
    color: '#4CAF50'
  },
  iconContainerNegative: {
    backgroundColor: '#FFEBEE',
    color: '#F44336'
  },
  iconContainerNeutral: {
    backgroundColor: '#FFF3E0',
    color: '#FF9800'
  },
  chartCard: {
    padding: theme.spacing(3),
    borderRadius: '12px',
    border: '1px solid #E0E0E0',
    boxShadow: 'none',
    height: '100%'
  },
  clientItem: {
    border: '1px solid #f0f0f0',
    padding: theme.spacing(2),
    borderRadius: '8px',
    marginBottom: theme.spacing(1),
    backgroundColor: '#fafafa',
  },
  clientItemNew: {
    borderColor: '#4CAF50',
    backgroundColor: '#F3F8F3',
        marginBottom: '1rem',
    borderRadius: '10px'
  },
  clientItemDropped: {
    borderColor: '#F44336',
    backgroundColor: '#FFF3F3',
        marginBottom: '1rem',
    borderRadius: '10px'
  },
  statusBadge: {
    fontSize: '0.75rem',
    fontWeight: 600,
    borderRadius: '12px'
  },
  statusBadgeNew: {
    backgroundColor: '#E8F5E9',
    color: '#2E7D32'
  },
  statusBadgeDropped: {
    backgroundColor: '#FFEBEE',
    color: '#C62828'
  },
  indicatorCard: {
    padding: theme.spacing(2),
    borderRadius: '12px',
    border: '1px solid #E0E0E0',
    marginBottom: theme.spacing(1)
  },
  indicatorCardNew: {
    borderColor: '#F44336',
    backgroundColor: '#FFF3F3'
  },
  indicatorCardResolved: {
    borderColor: '#4CAF50',
    backgroundColor: '#F3F8F3'
  },
  executiveSummary: {
    padding: theme.spacing(3),
    borderRadius: '12px',
    border: '1px solid #E0E0E0',
    backgroundColor: 'white'
  },
  summaryIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    backgroundColor: '#FFF3E0',
    color: '#FF9800',
    marginRight: theme.spacing(2),
    flexShrink: 0
  }
}))

const ArBalanceComparison = ({ comparisonData, currentArBalanceData }) => {
  const classes = useStyles()
  if (!comparisonData) {
    return (
      <Box className={classes.container}>
        <Typography variant="h6" style={{ textAlign: 'center', marginTop: '2rem' }}>
          {strings.AR_BALANCE_COMPARISON_NO_DATA}
        </Typography>
      </Box>
    )
  }

  const { comparison_report } = comparisonData
  const { overall_summary_change, top5_clients_comparison, health_indicator_status, aging_brackets_change } = comparison_report

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  // Format percentage
  const formatPercentage = (value) => {
    return `${value > 0 ? '+' : ''}${value.toFixed(2)}%`
  }

  const calculateTrendData = () => {
    const totalARChange = overall_summary_change.total_AR_change
    const concentrationChange = overall_summary_change.concentration_percentage_change
    
    // Get current values from the current AR balance data
    const currentTotalAR = currentArBalanceData?.totals?.totalARBalance || 0
    const currentConcentration = currentArBalanceData?.customerConcentration?.top5ClientsConcentration || 0
    
    // Calculate previous values by reverse engineering from deltas
    const previousTotalAR = currentTotalAR - totalARChange.absolute_delta
    const previousConcentration = currentConcentration - concentrationChange.absolute_delta
    
    return [
      { 
        period: `${strings.AR_BALANCE_COMPARISON_PREVIOUS_PERIOD} (${comparisonData.date1.split('T')[0]})`, 
        totalAR: Math.max(0, previousTotalAR), 
        concentration: Math.max(0, previousConcentration)
      },
      { 
        period: `${strings.AR_BALANCE_COMPARISON_CURRENT_PERIOD} (${comparisonData.date2.split('T')[0]})`, 
        totalAR: currentTotalAR, 
        concentration: currentConcentration
      }
    ]
  }

  const trendData = calculateTrendData()

  const arTrendOptions = {
    chart: { type: 'line', toolbar: { show: false }, fontFamily: 'inherit' },
    stroke: { width: 3, curve: 'smooth' },
    colors: ['#6f5bd8'],
    xaxis: {
      categories: trendData.map(d => d.period),
      labels: { style: { colors: '#9e9e9e', fontSize: '12px' } }
    },
    yaxis: {
      labels: {
        style: { colors: '#9e9e9e' },
        formatter: (val) => `$${(val/1000).toFixed(0)}K`
      }
    },
    grid: { strokeDashArray: 4, borderColor: '#f0f0f0' },
    tooltip: {
      y: { formatter: (val) => formatCurrency(val) }
    },
    markers: { size: 6 }
  }

  const concentrationTrendOptions = {
    chart: { type: 'line', toolbar: { show: false }, fontFamily: 'inherit' },
    stroke: { width: 3, curve: 'smooth' },
    colors: ['#2f7d63'],
    xaxis: {
      categories: trendData.map(d => d.period),
      labels: { style: { colors: '#9e9e9e', fontSize: '12px' } }
    },
    yaxis: {
      labels: {
        style: { colors: '#9e9e9e' },
        formatter: (val) => `${val.toFixed(1)}%`
      }
    },
    grid: { strokeDashArray: 4, borderColor: '#f0f0f0' },
    tooltip: {
      y: { formatter: (val) => `${val.toFixed(2)}%` }
    },
    markers: { size: 6 }
  }

  return (
    <div className={classes.container}>
      <Grid container spacing={3} className={classes.section}>
        <Grid item xs={12} md={4}>
          <Paper className={`${classes.summaryCard} ${overall_summary_change.total_AR_change.absolute_delta >= 0 ? classes.summaryCardPositive : classes.summaryCardNegative}`}>
            <div className={`${classes.iconContainer} ${overall_summary_change.total_AR_change.absolute_delta >= 0 ? classes.iconContainerPositive : classes.iconContainerNegative}`}>
              <TrendingUpIcon fontSize="large" />
            </div>
            <Typography variant="h4" className={`${classes.metricValue} ${overall_summary_change.total_AR_change.absolute_delta >= 0 ? classes.metricValuePositive : classes.metricValueNegative}`}>
              {formatPercentage(overall_summary_change.total_AR_change.percentage_change)}
            </Typography>
            <Typography className={classes.metricLabel}>{strings.AR_BALANCE_COMPARISON_TOTAL_AR_CHANGE}</Typography>
            <Typography className={classes.metricSubtext}>
              {formatCurrency(overall_summary_change.total_AR_change.absolute_delta)} {overall_summary_change.total_AR_change.absolute_delta >= 0 ? strings.AR_BALANCE_COMPARISON_INCREASE : 'decrease'}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper className={`${classes.summaryCard} ${overall_summary_change.concentration_percentage_change.absolute_delta <= 0 ? classes.summaryCardPositive : classes.summaryCardNegative}`}>
            <div className={`${classes.iconContainer} ${overall_summary_change.concentration_percentage_change.absolute_delta <= 0 ? classes.iconContainerPositive : classes.iconContainerNegative}`}>
              <TrendingDownIcon fontSize="large" />
            </div>
            <Typography variant="h4" className={`${classes.metricValue} ${overall_summary_change.concentration_percentage_change.absolute_delta <= 0 ? classes.metricValuePositive : classes.metricValueNegative}`}>
              {formatPercentage(overall_summary_change.concentration_percentage_change.percentage_change)}
            </Typography>
            <Typography className={classes.metricLabel}>{strings.AR_BALANCE_CONCENTRATION_RISK}</Typography>
            <Typography className={classes.metricSubtext}>
              {Math.abs(overall_summary_change.concentration_percentage_change.absolute_delta).toFixed(2)}% {overall_summary_change.concentration_percentage_change.absolute_delta <= 0 ? strings.AR_BALANCE_REDUCTION : strings.AR_BALANCE_INCREASE}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper className={`${classes.summaryCard} ${overall_summary_change.financial_health_rating_trend === 'Improved' ? classes.summaryCardPositive : classes.summaryCardNegative}`}>
            <div className={`${classes.iconContainer} ${overall_summary_change.financial_health_rating_trend === 'Improved' ? classes.iconContainerPositive : classes.iconContainerNegative}`}>
              {overall_summary_change.financial_health_rating_trend === 'Improved' ? <TrendingUpIcon fontSize="large" /> : <TrendingDownIcon fontSize="large" />}
            </div>
            <Typography variant="h4" className={`${classes.metricValue} ${overall_summary_change.financial_health_rating_trend === 'Improved' ? classes.metricValuePositive : classes.metricValueNegative}`}>
              {overall_summary_change.financial_health_rating_trend}
            </Typography>
            <Typography className={classes.metricLabel}>{strings.AR_BALANCE_HEALTH_TREND}</Typography>
            <Typography className={classes.metricSubtext}>
              {strings.AR_BALANCE_PERIOD_OVER_PERIOD}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Box className={classes.section}>
        <Paper className={classes.executiveSummary}>
          <div style={{ display: 'flex', alignItems: 'flex-start' }}>
            <div className={classes.summaryIcon}>
              <AssessmentIcon fontSize="large" />
            </div>
            <div style={{ flex: 1 }}>
              <Typography variant="h6" style={{ fontWeight: 600, marginBottom: '8px' }}>
                {strings.AR_BALANCE_COMPARISON_EXECUTIVE_SUMMARY}
              </Typography>
              <Typography variant="body2" style={{ lineHeight: 1.6, color: '#555' }}>
                {overall_summary_change.top_takeaway_change}
              </Typography>
            </div>
          </div>
        </Paper>
      </Box>

      <Grid container spacing={3} className={classes.section}>
        <Grid item xs={12} md={6}>
          <Paper className={classes.chartCard}>
            <Typography variant="h6" style={{ fontWeight: 600, marginBottom: '16px' }}>
              {strings.AR_BALANCE_COMPARISON_TOTAL_AR_TREND}
            </Typography>
            <Chart 
              options={arTrendOptions} 
              series={[{ name: 'Total AR', data: trendData.map(d => d.totalAR) }]} 
              type="line" 
              height={300} 
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper className={classes.chartCard}>
            <Typography variant="h6" style={{ fontWeight: 600, marginBottom: '16px' }}>
              {strings.AR_BALANCE_CONCENTRATION_RISK_TREND}
            </Typography>
            <Chart 
              options={concentrationTrendOptions} 
              series={[{ name: 'Concentration %', data: trendData.map(d => d.concentration) }]} 
              type="line" 
              height={300} 
            />
          </Paper>
        </Grid>
      </Grid>

      <Box className={classes.section}>
        <Paper className={classes.chartCard}>
          <Typography variant="h6" style={{ fontWeight: 600, marginBottom: '16px' }}>
            {strings.AR_BALANCE_COMPARISON_CLIENT_ANALYSIS}
          </Typography>
          <List disablePadding>
            {top5_clients_comparison.map((client, index) => {
              const isNew = client.status_change.includes('New')
              const isDropped = client.status_change.includes('Dropped')
              const isPersistent = client.status_change.includes('Persistent')
              const itemClass = isNew ? classes.clientItemNew : isDropped ? classes.clientItemDropped : classes.clientItem
              const badgeClass = isNew ? classes.statusBadgeNew : classes.statusBadgeDropped
              
              return (
                <ListItem key={index} className={itemClass}>
                  <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <div style={{ flex: 1 }}>
                      <Typography variant="body1" style={{ fontWeight: 600 }}>
                        {client.name}
                      </Typography>
                      <Chip 
                        label={client.status_change} 
                        size="small"
                        className={`${classes.statusBadge} ${isPersistent ? '' : badgeClass}`}
                        style={isPersistent ? { backgroundColor: '#E3F2FD', color: '#1976D2' } : {}}
                      />
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <Typography variant="body1" style={{ fontWeight: 700 }}>
                        {formatCurrency(client.total_AR_change.report_2_value)}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {strings.AR_BALANCE_FROM} {formatCurrency(client.total_AR_change.report_1_value)}
                      </Typography>
                      {client.total_AR_change.absolute_delta !== 0 && (
                        <Typography 
                          variant="body2" 
                          style={{ 
                            color: client.total_AR_change.absolute_delta > 0 ? '#4CAF50' : '#F44336',
                            fontSize: '0.75rem'
                          }}
                        >
                          {client.total_AR_change.absolute_delta > 0 ? '+' : ''}{formatCurrency(client.total_AR_change.absolute_delta)}
                        </Typography>
                      )}
                    </div>
                  </div>
                </ListItem>
              )
            })}
          </List>
        </Paper>
      </Box>

      {/* Risk Indicators */}
      <Grid container spacing={3} className={classes.section}>
        <Grid item xs={12} md={6}>
          <Paper className={classes.chartCard}>
            <Typography variant="h6" style={{ fontWeight: 600, marginBottom: '16px' }}>
              {strings.AR_BALANCE_COMPARISON_NEW_INDICATORS}
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: '16px' }}>
              {strings.AR_BALANCE_EMERGING_CONCERNS}
            </Typography>
            {health_indicator_status.new_indicators.map((indicator, index) => (
              <div key={index} className={`${classes.indicatorCard} ${classes.indicatorCardNew}`}>
                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                  <WarningIcon style={{ color: '#F44336', marginRight: '12px', marginTop: '2px' }} />
                  <Typography variant="body2" style={{ lineHeight: 1.5 }}>
                    {indicator}
                  </Typography>
                </div>
              </div>
            ))}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper className={classes.chartCard}>
            <Typography variant="h6" style={{ fontWeight: 600, marginBottom: '16px' }}>
              {strings.AR_BALANCE_COMPARISON_RESOLVED_INDICATORS}
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: '16px' }}>
              {strings.AR_BALANCE_IMPROVED_CONDITIONS}
            </Typography>
            {health_indicator_status.resolved_indicators.map((indicator, index) => (
              <div key={index} className={`${classes.indicatorCard} ${classes.indicatorCardResolved}`}>
                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                  <CheckCircleIcon style={{ color: '#4CAF50', marginRight: '12px', marginTop: '2px' }} />
                  <Typography variant="body2" style={{ lineHeight: 1.5 }}>
                    {indicator}
                  </Typography>
                </div>
              </div>
            ))}
          </Paper>
        </Grid>
      </Grid>
    </div>
  )
}

ArBalanceComparison.propTypes = {
  comparisonData: PropTypes.object,
  currentArBalanceData: PropTypes.object
}

export default ArBalanceComparison